import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { updateMetaTags } from '../utils/seo'

const IPhoneDisplayReparaturPage: React.FC = () => {
  const navigate = useNavigate()
  const go = (path: string) => { navigate(path); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50) }

  useEffect(() => {
    updateMetaTags({
      title: 'iPhone Display Reparatur – Alle Modelle & Preise | PhoneMedic',
      description: 'iPhone Display Reparatur ✓ Alle Modelle iPhone 16 bis SE ✓ OLED & Super Retina XDR ✓ ab 79€ ✓ 12 Monate Garantie ✓ Express 24h ✓ Bundesweiter Versand | PhoneMedic',
      keywords: ['iPhone Display Reparatur', 'iPhone Bildschirm kaputt', 'iPhone Display tauschen', 'iPhone Display Kosten', 'iPhone Glas gesprungen', 'iPhone OLED Reparatur'],
      canonicalUrl: 'https://www.phone-medic.de/iphone-display-reparatur',
    })

    const schema = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'iPhone Display Reparatur',
        description: 'Professionelle iPhone Display Reparatur für alle Modelle. OLED, Super Retina XDR, 12 Monate Garantie, Express 24h Service.',
        provider: { '@type': 'LocalBusiness', name: 'PhoneMedic', url: 'https://www.phone-medic.de', telephone: '+491605010907' },
        offers: [
          { '@type': 'Offer', name: 'iPhone 11 Display', price: '99.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
          { '@type': 'Offer', name: 'iPhone 13 Display', price: '129.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
          { '@type': 'Offer', name: 'iPhone 15 Pro Display', price: '199.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
        ],
        areaServed: 'DE',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Was kostet ein iPhone Display Tausch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Die Preise variieren je nach Modell: iPhone SE ab 79€, iPhone 11 ab 99€, iPhone 12/13 ab 119–129€, iPhone 14 ab 139€, iPhone 15 ab 149€, iPhone 16 ab 169€. Pro-Modelle sind etwas teurer. Alle Preise inklusive Einbau und 12 Monate Garantie.' },
          },
          {
            '@type': 'Question',
            name: 'Verliere ich Daten beim iPhone Display Tausch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Nein. Beim Display-Tausch wird ausschließlich der Bildschirm ausgewechselt. Alle Daten, Apps und Einstellungen bleiben vollständig erhalten.' },
          },
          {
            '@type': 'Question',
            name: 'Funktioniert Face ID nach dem Display-Tausch noch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Ja. Face ID sitzt nicht im Display, sondern in der TrueDepth-Kamera oben am iPhone. Der Display-Tausch hat keinen Einfluss auf Face ID.' },
          },
          {
            '@type': 'Question',
            name: 'Wie lange dauert ein iPhone Display Tausch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Der eigentliche Tausch dauert 45–90 Minuten. Inklusive Versandzeit rechne mit 3–5 Werktagen. Express-Service 24h auf Anfrage.' },
          },
          {
            '@type': 'Question',
            name: 'Mein iPhone-Glas ist gesprungen aber Display funktioniert – muss ich tauschen?',
            acceptedAnswer: { '@type': 'Answer', text: 'Nicht zwingend sofort, aber empfehlenswert. Risse im Glas können Feuchtigkeit und Staub eindringen lassen, was zu weiteren Schäden führt. Außerdem können sich Splitter lösen.' },
          },
        ],
      },
    ]

    let script = document.querySelector('#iphone-display-schema') as HTMLScriptElement
    if (!script) {
      script = document.createElement('script')
      script.id = 'iphone-display-schema'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(schema)
    return () => { document.querySelector('#iphone-display-schema')?.remove() }
  }, [])

  const prices = [
    { model: 'iPhone SE (2. & 3. Generation)', price: 'ab 79 €' },
    { model: 'iPhone 11', price: 'ab 99 €' },
    { model: 'iPhone 12 / 12 mini', price: 'ab 119 €' },
    { model: 'iPhone 12 Pro / 12 Pro Max', price: 'ab 129 €' },
    { model: 'iPhone 13 / 13 mini', price: 'ab 119 €' },
    { model: 'iPhone 13 Pro / 13 Pro Max', price: 'ab 139 €' },
    { model: 'iPhone 14 / 14 Plus', price: 'ab 139 €' },
    { model: 'iPhone 14 Pro / 14 Pro Max', price: 'ab 169 €' },
    { model: 'iPhone 15 / 15 Plus', price: 'ab 149 €' },
    { model: 'iPhone 15 Pro / 15 Pro Max', price: 'ab 199 €' },
    { model: 'iPhone 16 / 16 Plus', price: 'ab 169 €' },
    { model: 'iPhone 16 Pro / 16 Pro Max', price: 'ab 219 €' },
  ]

  const damages = [
    { title: 'Gesprungenes Glas', desc: 'Risse oder Splittern durch Sturz. Das Glas ist zerbrochen, aber das Display zeigt noch ein Bild. Tausch empfohlen um weitere Schäden durch eindringende Feuchtigkeit zu vermeiden.' },
    { title: 'Schwarzer Bildschirm', desc: 'Das iPhone reagiert und vibriert, der Bildschirm bleibt aber schwarz. Ein Zeichen für ein defektes OLED-Panel oder einen gelösten Displaystecker.' },
    { title: 'Farbfehler & Streifen', desc: 'Horizontale oder vertikale Streifen, verfärbte Bereiche oder flackernder Bildschirm – typische Symptome eines beschädigten OLED-Panels.' },
    { title: 'Touch funktioniert nicht', desc: 'Das Display zeigt ein Bild, reagiert aber nicht auf Berührungen. Kann ein Displayschaden oder ein Steckerproblem sein.' },
    { title: 'Flecken & Schatten', desc: 'Dunkle Flecken, "Tintenflecken" oder Schatten auf dem Display – häufig nach einem Sturz oder Druck auf das Display.' },
    { title: 'Wasserschaden am Display', desc: 'Feuchtigkeit unter dem Glas hinterlässt Beschläge oder "Wolken". Das Display muss getauscht werden.' },
  ]

  const faqs = [
    { q: 'Was kostet ein iPhone Display Tausch?', a: 'iPhone SE ab 79€, iPhone 11 ab 99€, iPhone 12/13 ab 119–129€, iPhone 14 ab 139€, iPhone 15 ab 149€, iPhone 16 ab 169€. Pro-Modelle etwas mehr. Alle Preise inkl. Einbau und 12 Monate Garantie.' },
    { q: 'Verliere ich Daten beim Display-Tausch?', a: 'Nein. Beim Display-Tausch wird ausschließlich der Bildschirm gewechselt. Alle Daten bleiben erhalten.' },
    { q: 'Funktioniert Face ID nach dem Display-Tausch noch?', a: 'Ja. Face ID sitzt in der TrueDepth-Kamera, nicht im Display. Der Tausch hat keinen Einfluss auf Face ID.' },
    { q: 'Wie lange dauert ein iPhone Display Tausch?', a: '45–90 Minuten. Inklusive Versand 3–5 Werktage. Express 24h auf Anfrage.' },
    { q: 'Glas gesprungen aber Display funktioniert – muss ich tauschen?', a: 'Nicht sofort, aber empfehlenswert. Risse lassen Feuchtigkeit und Staub eindringen und können sich ausweiten.' },
    { q: 'Welche Display-Qualität wird verbaut?', a: 'Wir verbauen Displays in hochwertiger OEM-Qualität. Die Farbdarstellung, Helligkeit und Touch-Empfindlichkeit entsprechen dem Original.' },
  ]

  return (
    <div className="min-h-screen bg-white">
      <section
        className="relative text-white py-20"
        style={{ backgroundImage: 'url(/hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center top' }}
      >
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 text-sm font-medium px-3 py-1 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></span>
              OLED · Super Retina XDR · alle Modelle
            </div>
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              iPhone Display Reparatur –<br />
              <span className="text-indigo-400">Alle Modelle & Preise</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              iPhone-Glas gesprungen, schwarzer Bildschirm oder Touch reagiert nicht? Wir tauschen Displays für alle iPhone Modelle – professionell, mit 12 Monaten Garantie, bundesweiter Einsende-Service.
            </p>
            <div className="flex flex-wrap gap-4">
              <button onClick={() => go('/repair')} className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
                Modell wählen & Preis prüfen
              </button>
              <a href="mailto:service@phone-medic.de" className="bg-white/10 text-white border border-white/20 px-7 py-3.5 rounded-lg font-semibold hover:bg-white/20 transition-colors">
                Per E-Mail anfragen
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-800 text-white py-5 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { number: 'ab 79 €', label: 'Display-Tausch' },
              { number: '45–90 min', label: 'Reparaturzeit' },
              { number: '12 Monate', label: 'Garantie' },
              { number: 'Bundesweit', label: 'Einsende-Service' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-2xl font-bold text-indigo-400">{s.number}</div>
                <div className="text-sm text-gray-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Welche Displayschäden können wir beheben?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {damages.map((d, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-2">{d.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Preise nach Modell</h2>
            <p className="text-gray-500">Alle Preise inklusive Einbau und 12 Monate Garantie</p>
          </div>
          <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-2 bg-gray-800 text-white px-6 py-3 text-sm font-semibold">
              <span>Modell</span>
              <span className="text-right">Preis</span>
            </div>
            {prices.map((p, i) => (
              <div key={i} className={`grid grid-cols-2 px-6 py-4 text-sm border-b border-gray-200 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                <span className="text-gray-700 font-medium">{p.model}</span>
                <span className="text-right font-bold text-indigo-600">{p.price}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3 text-center">Exakte Preise für dein Modell direkt im Reparatur-Konfigurator.</p>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Weitere iPhone Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'iPhone Akku tauschen', path: '/iphone-akku-tauschen' },
              { label: 'iPhone startet nicht', path: '/iphone-startet-nicht' },
              { label: 'iPhone Neustart-Schleife', path: '/iphone-neustart-schleife' },
              { label: 'Alle iPhone Reparaturen', path: '/repair' },
            ].map((item) => (
              <button key={item.path} onClick={() => go(item.path)} className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm font-medium text-gray-700 hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 transition-colors text-center">
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3">iPhone Display kaputt? Wir reparieren es.</h2>
          <p className="text-gray-400 mb-8">Alle Modelle · ab 79 € · 12 Monate Garantie · Bundesweiter Einsende-Service</p>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => go('/repair')} className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
              Modell auswählen & buchen
            </button>
            <a href="mailto:service@phone-medic.de" className="bg-white text-gray-900 px-7 py-3.5 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Per E-Mail anfragen
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default IPhoneDisplayReparaturPage
